'use client';
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';

import { formatTickerToURLFriendly } from '@benzinga/utils';
import type { NetBuySellDatum } from './utils';
import { getNetBuySellByWindow } from '../trades-actions';
import { NET_BUY_SELL_DEFAULT_PRESET, NET_BUY_SELL_WINDOWS } from './netBuySellWindows';
import styles from './NetBuySellBar.module.scss';

type NetBuySellBarProps = {
  initialData: NetBuySellDatum[];
  initialPreset?: string;
};

const AXIS_FRACS = [1, 0.5, 0.25] as const;

const NetBuySellBar = ({ initialData, initialPreset = NET_BUY_SELL_DEFAULT_PRESET }: NetBuySellBarProps) => {
  const [preset, setPreset] = useState(initialPreset);
  const [data, setData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    for (const window of NET_BUY_SELL_WINDOWS) {
      if (window.value !== initialPreset) {
        void getNetBuySellByWindow(window.value);
      }
    }
  }, [initialPreset]);

  const handleChange = async (value: string) => {
    setPreset(value);
    setIsLoading(true);
    try {
      setData(await getNetBuySellByWindow(value));
    } finally {
      setIsLoading(false);
    }
  };

  const maxSide = Math.max(1, ...data.map(row => Math.max(row.buys, row.sells)));
  const buyTotal = useMemo(() => data.reduce((sum, row) => sum + row.buys, 0), [data]);
  const sellTotal = useMemo(() => data.reduce((sum, row) => sum + row.sells, 0), [data]);
  const windowCaption = NET_BUY_SELL_WINDOWS.find(window => window.value === preset)?.caption ?? 'last 90 days';

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>Most Bought and Sold Tickers</h2>
          <p className={styles.subtitle}>
            {data.length} most active by trade count · {windowCaption}
          </p>
        </div>
        <div className={styles.headerMeta}>
          <div className={styles.totals}>
            <div className={styles.total}>
              <span className={styles.totalLabel}>Sells</span>
              <span className={styles.totalNeg}>{sellTotal}</span>
            </div>
            <div className={styles.totalDivider} />
            <div className={styles.total}>
              <span className={styles.totalLabel}>Buys</span>
              <span className={styles.totalPos}>{buyTotal}</span>
            </div>
          </div>
          <label className={styles.select}>
            <select
              aria-label="Time period"
              disabled={isLoading}
              onChange={event => handleChange(event.target.value)}
              value={preset}
            >
              {NET_BUY_SELL_WINDOWS.map(window => (
                <option key={window.value} value={window.value}>
                  {window.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      {data.length === 0 ? (
        <div className={styles.empty}>No disclosed trades in this period.</div>
      ) : (
        <div className={`${styles.body} ${isLoading ? styles.loading : ''}`}>
          <div className={styles.rows}>
            {data.map(row => {
              const sellPct = Math.min(50, (row.sells / maxSide) * 50);
              const buyPct = Math.min(50, (row.buys / maxSide) * 50);
              const href = `/government-trades/securities/${formatTickerToURLFriendly(row.ticker)}`;

              return (
                <div className={styles.row} key={row.ticker}>
                  <div className={styles.ticker}>
                    <Link className={styles.tickerLink} href={href}>
                      <span className={styles.tickerSymbol}>{row.ticker}</span>
                      <span className={styles.tickerName}>{row.company}</span>
                    </Link>
                  </div>
                  <div className={styles.barArea}>
                    <div className={styles.track} title={`Sells ${row.sells} · Buys ${row.buys}`}>
                      {AXIS_FRACS.map(frac => (
                        <span
                          key={`tick-sell-${frac}`}
                          className={styles.tick}
                          style={{ left: `${50 - frac * 50}%` }}
                        />
                      ))}
                      {AXIS_FRACS.map(frac => (
                        <span
                          key={`tick-buy-${frac}`}
                          className={styles.tick}
                          style={{ left: `${50 + frac * 50}%` }}
                        />
                      ))}
                      <div className={styles.axis} />
                      {row.sells > 0 ? (
                        <div className={styles.barSell} style={{ width: `${sellPct}%`, left: `${50 - sellPct}%` }} />
                      ) : null}
                      {row.buys > 0 ? (
                        <div className={styles.barBuy} style={{ width: `${buyPct}%`, left: '50%' }} />
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.values}>
                    <span className={styles.valueNeg}>{row.sells}</span>
                    <span className={styles.valueSep}>/</span>
                    <span className={styles.valuePos}>{row.buys}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className={styles.axisRow}>
            <div className={styles.axisSpacer} />
            <div className={styles.axisScale}>
              {[...AXIS_FRACS].map((frac, index) => (
                <span key={`sell-axis-${index}`} className={styles.axisTick} style={{ left: `${50 - frac * 50}%` }}>
                  {Math.round(frac * maxSide)}
                </span>
              ))}
              <span className={styles.axisZero} style={{ left: '50%' }}>
                0
              </span>
              {[...AXIS_FRACS].reverse().map((frac, index) => (
                <span key={`buy-axis-${index}`} className={styles.axisTick} style={{ left: `${50 + frac * 50}%` }}>
                  {Math.round(frac * maxSide)}
                </span>
              ))}
            </div>
            <div className={styles.valueSpacer} />
          </div>
        </div>
      )}
    </section>
  );
};

export default NetBuySellBar;
